import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import { memo, useState, useMemo, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import ChatBot, { ChatBotProvider } from "react-chatbotify";
import { api } from '../../api-client';
import { ChatSessionType, UserType } from '../../types/appointment-types';
import { useAuthStore } from "@apps/common/store/useAuthStore";
import { Queue } from 'queue-typescript';
import 'react-toastify/dist/ReactToastify.css';
import config from "@/config";
import { useStyles, useSettings, useToasts, useMessages, usePaths, useTextArea } from "react-chatbotify";
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { useRequest } from "ahooks";

const MEDIA_URL = config.ENV === "dev" ? 'http://localhost:1337' : ''

async function delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

interface ChildComponentProps {
    chatSessionToken: string,
    member?: any;
    coach?: any;
    refreshChatToken?: () => void;
    persona?: string;
}



interface ChildHandle {
    childFunction: (userInput: string) => void;
}

const ChatSocket = forwardRef<ChildHandle, ChildComponentProps>((props: any, ref) => {

    const socketUrl = useCallback(() => config.CHAT_SERVER, []);
    //const [chatSession, setChatSession] = useState<Partial<ChatSessionType> | undefined>()
    //const [coach, setCoach] = useState<any>()
    //const [message, setMessage] = useState<string>()
    const { messages, injectMessage, removeMessage } = useMessages();
    const { goToPath } = usePaths();
    const { dismissToast, showToast, toasts } = useToasts();
    const chatSessionInitialized = useRef<boolean>(false)
    const { updateSettings } = useSettings();
    const { toggleTextAreaDisabled } = useTextArea();

    ////console.debug(`props - ${JSON.stringify(props)}`)

    const options = useMemo(() => {
        //console.debug(`ChatSocket useMemo`)
        const options =
        {
            protocols: props.chatSessionToken,
            //onMessage: ()=> {//console.debug(`received message`)}
            onError: (event: any) => {
                toasts.forEach((toast) => {
                    dismissToast(toast?.id)
                })

                showToast(`An error occured while connecting to the chat session ! Trying to reconnect`)
                //console.debug(`An error occured while communicating with the WebSocket ${JSON.stringify(event)}`)

            },
            onReconnectStop: (numAttempts: number) => {
                console.error(`WebSocket reconnect attempts FAILED - ${numAttempts}`)
            },
            retryOnError: true,
            reconnectAttempts: 3
        }
        return options

    }, [])
    const { readyState, sendMessage, lastMessage } = useWebSocket(socketUrl, options)

    const sendUserInput = (userInput: string) => {
        //console.debug(`userInput - ${userInput}`);
        sendMessage(userInput)
    };

    useImperativeHandle(ref, () => ({
        childFunction: sendUserInput,
    }));

    const initializeChatSession = () => {
        //console.debug(`initializing ChatSession`)
        messages.forEach(
            (msg) => {
                removeMessage(msg.id)
            }

        )
        updateSettings({
            botBubble: {
                avatar: props?.persona === 'member' ? MEDIA_URL + props?.coach?.profilePicture?.url : MEDIA_URL + props?.member?.profilePicture?.url
            },
            chatInput: {
                disabled: false
            },
            userBubble: {
                avatar: props?.persona === 'member' ? MEDIA_URL + props?.member?.profilePicture?.url : MEDIA_URL + props?.coach,
                showAvatar: true
            }

        }
        )
        chatSessionInitialized.current = true
        goToPath('loop')

        // Update the avatar of the bot to that of the Coach
    }

    const pauseChatSession = () => {
        //console.debug(`pausing ChatSession`)
        toasts.forEach(
            (toast) => {
                dismissToast(toast.id)
            }

        )


        // Update the avatar of the bot to that of the Coach
    }


    //const INTRO_MEMBER_MSG = `Hello ${props?.user?.firstName}! 👋! Welcome to SkylerHealth's Coaching Session with ${props?.coach?.firstName}. I'm excited that you are using our services 😊!`
    //const INTRA_COACH_MSG = `Hello ${props?.coach?.firstName}! 👋! You will be coaching ${props?.user?.firstName} today.`
    const CONNECTION_CHAT_MSG = `Sit tight! We are connecting your chat session.`
    const CHAT_SESSION_AWAITING_MEMBERS = `Give us a few moments! We are waiting for everyone to join this session.`
    const CHAT_SESSION_CONNECTED = `You are now connected to your chat session! I will take your leave and let you resume your session with ${props?.coach?.firstName}. Goodbye!`

    useEffect(
        () => {
            if ([ReadyState.CONNECTING, ReadyState.UNINSTANTIATED].includes(readyState)) {

                updateSettings(
                    {
                        chatInput: {
                            disabled: true
                        }
                    }
                )

                injectMessage(CONNECTION_CHAT_MSG)

            }

        }, []
    )

    useEffect(
        () => {

            switch (readyState) {
                case ReadyState.OPEN: {
                    //console.debug(`WebSocket Open`)
                    if (chatSessionInitialized.current === false)
                        injectMessage(CHAT_SESSION_AWAITING_MEMBERS)

                }
                    break;
                case ReadyState.CLOSED: {
                    //console.debug(`WebSocket closed`)
                    
                }
                    break;
                case ReadyState.CONNECTING: {
                    //console.debug(`WebSocket is connecting`)
                }
                    break;

                case ReadyState.UNINSTANTIATED: {
                    //console.debug(`WebSocket is uninstantiated`)
                }
                    break;
                case ReadyState.CLOSING: {
                    //console.debug(`WebSocket is closing`)
                }
                    break;
            }

        }, [sendMessage, readyState]
    )

    useEffect(
        () => {
            //console.debug(`received Message - ${(lastMessage?.data)}`)
            if ((lastMessage?.data === 'BEGIN_CHATSESSION') && (chatSessionInitialized.current === false)) {

                //console.debug(`Change the style of the bot`)
                setTimeout(initializeChatSession, 3000)

            }
            else if (lastMessage?.data === 'LOST_PARTCIPANT') {
                //console.debug(`LOST_PARTCIPANT`)
                /**
                 * We Need to post a toast and disable the chat session
                 * 
                 */

                toggleTextAreaDisabled()
                const lostPartcipant = props.persona === 'member' ? props.coach?.firstName : props.member.firstName
                showToast(`We seem to have ${lostPartcipant} disconnected from this session. Let's wait for ${lostPartcipant} to reconnect.`, 10000)

            }
            else if (lastMessage?.data === 'RESUME_CHATSESSION'){
                if (chatSessionInitialized.current === true){
                //const lostPartcipant = props.persona === 'member' ? props.coach?.firstName : props.member.firstName    
                toggleTextAreaDisabled()
                showToast(`... resuming chat session!`, 1000)
                }
                if (chatSessionInitialized.current === false){
                    setTimeout(initializeChatSession, 3000)
                }
            }
            else if (lastMessage?.data !== 'BEGIN_CHATSESSION') {
                ////console.debug(`BEGIN_CHATSESSION`)
                if (lastMessage?.data)
                    injectMessage(lastMessage?.data)
            }



        }, [lastMessage]
    )

    return (
        <>
            {/* <Button onClick={() => { handleClickSendMessage('TEST DFdkfhksjfsfg') }}>
            </Button> */}
        </>
    )
});


export const CoachSkyler: React.FC<{ chatSessionId: any, persona?: 'member' | 'provider' }> = ({ chatSessionId, persona }) => {

    const childRef = useRef<ChildHandle>(null);
    ////console.debug(`ChatSessionId -  ${JSON.stringify(props.chatSessionId)}`)
    //const [chatSession, setChatSession] = useState<Partial<ChatSessionType> | undefined>()

    const id = useRef<string | null>();
    const chatSessionRef = useRef<Partial<ChatSessionType> | null>();
    const { user, fetchUser } = useAuthStore();
    const [coach, setCoach] = useState<Partial<UserType> | null>()
    const [member, setMember] = useState<Partial<UserType> | null>()
    //const [connected, setConnected] = useState<boolean>(false)
    //const [socketConnectionRetries, setSocketConnectionRetries] = useState<number>(0)
    //const [endCoachingSession, setEndCoachingSession] = useState<boolean>(false)
    //const [toastId, setToastId] = useState<string>()
    const [endSessionMessage, setEndSessionMessage] = useState<string>()
    //const [beginSession, setBeginSession] = useState<boolean>(false)
    //const [chatSessionToken, setChatSessionToken] = useState<string | null>()
    const [chatBotIntialized, setChatBotInitalized] = useState<boolean>(false)

    let _params: any;

    const { data: chatSession, run: getChatSession } = useRequest(
        (chatSession) =>

            api.chatSession.find(parseInt(chatSession as string)).then(
                (res) => {
                    ////console.debug(`chatSession ServiceProvider Details - ${JSON.stringify(res?.appointment?.timeSlot?.serviceProvider?.user)}`)
                    setMember(res?.appointment?.memberService?.user)
                    setCoach(res?.appointment?.timeSlot?.serviceProvider?.user)
                    ////console.debug(`chatSession Member Details - ${JSON.stringify(res?.appointment?.memberService?.user)}`)
                    return res
                }
            ),
        {
            //refreshDeps: [chatSession?.chatSessionId],       
            ready: !!chatSessionId
        }
    );

    useMemo(() => {

        ////console.debug(`CoachSkyler useMemo - ${chatSessionId}`)

        if (chatSessionId) {
            id.current = chatSessionId
            //console.debug(`getting chatSession for ${chatSessionId}`)

            getChatSession(chatSessionId)

            ////console.debug (`chatSession details - ${JSON.stringify(_chatSession)}`)

            chatSessionRef.current = chatSession as ChatSessionType
            return chatSession
        }

    }, [chatSessionId]);

    const { data: _token, run: getChatSessionToken } = useRequest(
        () =>
            api.chatSession.join(chatSession?.chatSessionId ? chatSession.chatSessionId : ''),
        {
            //refreshDeps: [chatSession?.chatSessionId],       
            //ready: _chatSession?.chatSessionId ? _chatSession?.chatSessionId.length > 0 ? true : false
            ready: chatSession?.chatSessionId ? true : false,
            manual: true
        }
    );

    const chatToken: string = useMemo(() => {

        // if ( typeof chatSession === 'string'){
        //     //console.debug ('returning a null Token')
        //     return null
        // }

        if (_token)
            return _token;

        //const chatSession = chatSession as ChatSessionType
        if (chatSession?.chatSessionId) {
            ////console.debug (`Fetching chatSessionToken`)
            //console.debug(`Generating a new Chat Session Token for ${chatSession?.chatSessionId}`)
            getChatSessionToken()
            ////console.debug (`token ${_token}`)
            if (_token?.length > 0)
                return _token
        }

    }, [chatSession, _token])

    const END_SESSION_MESSAGES: Map<string, string> = new Map();
    END_SESSION_MESSAGES.set('ERR_NO_CHATSESSION_ID', 'We are having trouble accessing the chat session details. Please try to refresh or check if you are connected to the web')

    //const messageQueue = new Queue<string>();
    //const botMessageQueue = new Queue<string>();
    //const { showToast } = useToasts();

    useEffect(() => {

        // shows a toast for 3 seconds
        ////console.debug('show toasts')            
    }, [])

    const send_Tochannel = async (params: any) => {
        try {
            ////console.debug(`call_channel params ${JSON.stringify(params)}`)
            childRef.current?.childFunction(params?.userInput);
        } catch (error) {
            //console.debug(`error`)
        }
    }

    //const hasError = false;

    const flow = {

        start: {
            message:
                async (params: any) => {
                    //await params.dismissToast(toastId.current)
                    ////console.debug(`persona - ${persona}`)
                    if (persona === 'member')
                        await params.injectMessage(`Hello ${member?.firstName}! 👋! Welcome to SkylerHealth's Coaching Session with ${coach?.firstName}. I'm excited that you are using our services 😊!`)
                    else if (persona === 'provider')
                        await params.injectMessage(`Hello ${coach?.firstName}! 👋! We are waiting for ${member?.firstName} to join the session.`)
                    setChatBotInitalized(true);
                    //params.goToPath("connect_channel");
                },
            chatDisabled: true
            //transition: { duration: 1000 },

            //path: nextCoachingPath('start') 
        },

        loop: {
            function: async (params: any) => {
                //await params.dismissToast(toastId.current)
                await send_Tochannel(params);
            },
        },
        end: {
            //message: 'Hope you enjoyed this session! GoodBye 👋🏻 !',
            message: async (params: any) => {
                //await params.dismissToast(toastId.current)
                return `${endSessionMessage}`
            },
            chatDisabled: true
        }

    }

    const chat_settings = {
        chatInput: {
            botDelay: 500
        },
        header: {
            title: "Coaching with Skyler Health",
            showAvatar: false
        },
        chatHistory: { storageKey: chatSessionId },
        footer: {
            text: ""
        },
        chatWindow: {
            defaultOpen: true
        },
        botBubble: {
            showAvatar: true,
            avatar: '../../../../../src/assets/images/users/skylerbot.png',
            animate: false
        },
        userBubble: {
            showAvatar: true,
            animate: false
        },
        general: {

        }

    }

    return (
        <>
            {chatSessionId ?
                (
                    <>
                        {chatSession ? (
                            <ChatBotProvider>
                                <ChatBot id={persona === "member" ? "SkylerCoachingMember" : "SkylerCoachingCoach"} /*themes={themes}*/ settings={chat_settings} flow={flow} />
                                {chatBotIntialized && chatToken ?
                                    (
                                        <ChatSocket chatSessionToken={chatToken} persona={persona} member={member} coach={coach} ref={childRef} refreshChatToken={getChatSessionToken}></ChatSocket>
                                    ) : null
                                }
                            </ChatBotProvider>
                        ) : null
                        }
                    </>) : null
            }

        </>
    )

}

// function arePropsEqual(oldProps: { chatSessionId: string | null }, newProps: { chatSessionId: string | null }) {
//     //console.debug('compare props')
//     if (oldProps.chatSessionId === newProps.chatSessionId)
//         return true
//     else
//         return false

// }

export default CoachSkyler;